<template>
  <div class="Legend my-3" v-if="legendItems">
    <p class="pl-3 ml-3 mr-3">
      Bekijk de barcodes, focus op 1 van de 4 barcode eigenschappen, of bekijk de bouwblokken.
    </p>
    <div 
      class="pl-3 ml-3 mr-3">
      <Form
        class="d-flex w-100"
        @submit="() => null">
        <FormField 
          class="w-100"
          v-model="displaymodeModel"
          v-bind="displaymode" />
      </Form>
    </div>
    <div>
      <ul class="m-0 p-0 pl-3 ml-3 mb-3 list-unstyled" v-if="displaymodeModel !== 'bouwblok' && displaymodeModel !== 'bouwblok-auto'">
        <li v-for="item in legendItems" :key="item.id" class="d-flex my-2 mr-3 align-items-center">
          <span>
            <Hexagon style="position: absolute" color="#f6f6f4" />
            <Hexagon v-if="item.active" :color="item.color" :opacity="opacity" /> 
          </span> 
          <span class="mx-2 flex-grow-1">
            <span class="mr-2">{{ item.label }}</span>
            <FontAwesomeIcon v-if="item.tooltip" :icon="['fas', 'info-circle']" :content="item.tooltip" v-tippy="{ placement : 'right-end', interactive : true, delay: [0, 400] }" />
          </span>
          <span v-if="item.count !== null" class="">{{ item.count }}</span>
        </li>
      </ul>
      <div v-else class="pl-3 ml-3 mr-3 pr-3 mb-3 ">
        <strong>Let op:</strong> Bij de weergave van bouwblokken worden de filters genegeerd, omdat de bouwblokken gemixte data bevatten.
      </div>
    </div>
  </div>
</template>

<script>

import Form from 'molecule/form/Form'
import FormField from 'molecule/form/FormField'
import Hexagon from 'atom/Hexagon'

import { mapGetters, mapMutations } from 'vuex'

export default {
  components: {
    Form, FormField, Hexagon
  },
  data() {
    return {
      opacity: 0.75,
      displaymode: {
        type: 'select',
        label: null,
        novalidate: true,
        options: [
          {
            value: 'barcode',
            text: 'Barcode'
          },
          {
            value: 'eigendom',
            text: 'Eigendom'
          },
          {
            value: 'energielabel',
            text: 'Energielabel'
          },
          {
            value: 'wozwaardeklasse',
            text: 'WOZ Waarde Klasse'
          },
          {
            value: 'bouwjaarklasse',
            text: 'Bouwperiode Klasse'
          },
          {
            value: 'stedelijk_hitte_eiland_effect_25',
            text: 'Hitte eiland (25m)'
          },
          {
            value: 'stedelijk_hitte_eiland_effect_500',
            text: 'Hitte eiland (500m)'
          },
          {
            value: 'hittekaart_gevoelstemperatuur_25',
            text: 'Gevoelstemperatuur (25m)'
          },
          {
            value: 'hittekaart_gevoelstemperatuur_500',
            text: 'Gevoelstemperatuur (500m)'
          },
          {
            value: 'waterdiepte_bij_intense_neerslag_1_100_jaar_25',
            text: 'Waterdiepte bij neerslag t/m 100 jaar (25m)'
          },
          {
            value: 'waterdiepte_bij_intense_neerslag_1_1000_jaar_25',
            text: 'Waterdiepte bij neerslag t/m 1000 jaar (25m)'
          },
          {
            value: 'waterdiepte_bij_intense_neerslag_1_100_jaar_500',
            text: 'Waterdiepte bij neerslag t/m 100 jaar (500m)'
          },
          {
            value: 'waterdiepte_bij_intense_neerslag_1_1000_jaar_500',
            text: 'Waterdiepte bij neerslag t/m 1000 jaar (500m)'
          },
          {
            value: 'bouwblok',
            text: 'Bouwblokken (handmatig)'
          },
          {
            value: 'bouwblok-auto',
            text: 'Bouwblokken (automatisch)'
          },
        ]
      },
    }
  },
  computed: {
    ...mapGetters('map', [
      'getFilterStatus'
    ]),
    ...mapGetters('config', {
      layerConfig: 'layers'
    }),
    ...mapGetters('filters', [
      'getFilterInfo',
      'getActiveNeighborhood'
    ]),
    ...mapGetters('geodata', [
      'getVisibleBuildings',
      'hasActiveNeighborhoodBuildingData',
      'getTopVisibleBarcodes',
      'getRemainingVisibleBarcodes'
    ]),
    legendItems() {
      let filter = Object.assign({}, (this.getFilterInfo({ mode: this.displaymodeModel })));
      let items = filter.items || []
      
      if (this.hasActiveNeighborhoodBuildingData) {
      
        if (this.displaymodeModel !== 'barcode') {
          return items.map(item => {
            // Count the number of buildings matching to the active legend item labels
            item.count = this.getVisibleBuildings.filter(row => {
              return (filter.categorical ? row[`${this.displaymodeModel}_code`] : row[this.displaymodeModel]) === (item.value || item.label)
            }).length
            return item
          })
        } else {
          
          let top = this.getTopVisibleBarcodes({ count: items.length })
          
          let remaining = this.getRemainingVisibleBarcodes({ count: items.length })
          let remainingSum = remaining.reduce((result, building) => result + building.count, 0)  

          let activeComponents = Object.values(this.getFilterInfo({ mode: null })).filter(filter => filter.component && filter.enabled)
          
          // TODO: This bit of code is still killing off the 'bouwblok' filters from Vuex. Why ?!?!?!
          return items.slice(0, top.length).map((item, index) => {
            if (index === (top.length -1)) {
              item.label = 'Overige'
              item.count = remainingSum
              item.tooltip = `De som van ${remaining.length} barcode combinaties`
            } else {
              item.count = top[index].count
              item.label = top[index].barcode

              item.tooltip = this.generateTooltip({ 
                components: activeComponents, 
                barcode: top[index] 
              })
            }
            return item
          })
        }
      } else {
        return items.map(item => {
          item.count = ''
          return item
        })
      }
    },
    displaymodeModel: {
      get() {
        return this.getFilterStatus.displaymode
      },
      set (value) {
        this.updateFilterStatus({ displaymode: value })
      }
    },
  },
  mounted() {
    if (this.layerConfig.blokken_manual.active !== true) {
      let index = this.displaymode.options.findIndex(item => item.value === 'bouwblok')
      if (index > -1) {
        this.displaymode.options.splice(index, 1)
      }
    }
  },
  methods: {
    ...mapMutations('map', [
      'updateFilterStatus'
    ]),
    generateTooltip({ components, barcode }) {
      return components.map(component => {
        // e.g. 6 - WOZ Waarde Klasse: €300.000 of meer
        let code = barcode[`${component.id}_code`]
        let value = component.categorical ? component.items.find(item => item.code === code).tooltip : barcode[component.id];
        return `${code} - ${component.label}: ${value}`
      }).join(' <br />')
    }
  }
}
</script>

<style lang="scss">

.Legend {
  position: relative;
  
  &__title {
    height: 60px;
  }
  &__subtitle {
    height: 30px
  }
  ul li span:first-child {
    width: 20px;
    height: 23px;
  }
  ul li {
    line-height: 1
  }
}
// Due to large size of tooltip
.tippy-popper[x-placement^=right] .tippy-backdrop {
  border-radius: 0;
}

</style>
